import { useEffect, useMemo, useRef, useState } from "react";
import {
  type EmsLoadPoint,
  type EmsPlan,
  type EmsBalancing,
  type EnergyForecast,
  type ForecastResponse,
  type ForecastSlot,
  type Live,
  type PricingConfigResponse,
  type PlanHistory,
  type PricingToday,
  fetchForecast,
  fetchLive,
  fetchPlanHistory,
  fetchPricingConfig,
  fetchPricingToday,
} from "../api";
import { Card, Pill, ScreenHeader, SectionTitle, Skeleton } from "../components/ui";
import { SmartMomentsCard } from "../components/SmartMomentsCard";
import { batteryStateLabel, formatPricePerKwh } from "../format";
import {
  type ChartResolution,
  getChartResolution,
  getPriceIncludesTax,
} from "../prefs";

const POLL_MS = 5 * 60_000; // the Cerbo refreshes its plan every few minutes

/**
 * Prognose tab - HET BATTERIJPLAN: wat de accu de komende uren gaat
 * doen, getekend over de dagprijzen. Merkonafhankelijk: Victron-sites
 * uit de DESS-ring (die voert ons plan uit), elders uit de Slim
 * Stroom-planner. Header, "nu"-kaart, plankaart (SOC-steplijn over de
 * prijsbalken), lijst van komende acties; zonder plan een lege staat.
 * docs/customer-app.md > Front-end > Prognose tab
 */
export function Prognose() {
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasData = useRef(false);
  // 24u = today's view; 48u reaches into tomorrow once the day-ahead
  // prices are published (~13:00) - before that the line simply stops
  // where the schedule does.
  const [horizon, setHorizon] = useState<24 | 48 | 72>(24);
  const seq = useRef(0);

  // Price curve (today + tomorrow once published) - drawn as the bar
  // backdrop for the FULL chart window, so the bars keep going where
  // het schema eerder eindigt dan de prijsreeks. Respects the
  // customer's Prijzen-tab resolution pref (Account → Energiecontract →
  // "Tijdsblokken van 15 minuten"): 1h bars or 15-min bars.
  const [pricing, setPricing] = useState<PricingToday | null>(null);
  const [resolution, setResolution] = useState<ChartResolution>(getChartResolution());
  useEffect(() => {
    const onPref = () => setResolution(getChartResolution());
    window.addEventListener("prefs:chart-resolution", onPref);
    return () => window.removeEventListener("prefs:chart-resolution", onPref);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      const my = ++seq.current;
      try {
        const [v, p] = await Promise.all([
          fetchForecast(horizon),
          fetchPricingToday(resolution).catch(() => null),
        ]);
        if (!cancelled && my === seq.current) {
          setData(v);
          if (p) setPricing(p);
          hasData.current = true;
          setError(null);            // hersteld -> balk weg
        }
      } catch (e) {
        // Blip bij hervatten mag geen alarm zijn zolang er een plan staat.
        if (!cancelled && !hasData.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [horizon, resolution]);

  const [live, setLive] = useState<Live | null>(null);
  useEffect(() => {
    let cancelled = false;
    const get = () => fetchLive().then((v) => { if (!cancelled) setLive(v); }).catch(() => {});
    get();
    const id = window.setInterval(get, 30_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // All-in price transform - SAME rules as the Prijzen tab: only when the
  // "inclusief belastingen" pref is on AND the contract is dynamic, apply
  //   (epex + marge + energiebelasting) × (1 + btw)
  // (mirrors customer_app/pricing.py's import price - a linear function of
  // epex, not a flat surcharge).
  const [config, setConfig] = useState<PricingConfigResponse | null>(null);
  const [includesTax, setIncludesTax] = useState<boolean>(getPriceIncludesTax());
  useEffect(() => {
    let cancelled = false;
    fetchPricingConfig().then((c) => { if (!cancelled) setConfig(c); }).catch(() => {});
    const onPref = () => setIncludesTax(getPriceIncludesTax());
    window.addEventListener("prefs:price-includes-tax", onPref);
    return () => {
      cancelled = true;
      window.removeEventListener("prefs:price-includes-tax", onPref);
    };
  }, []);
  const cfg = config?.effective;
  const showAllIn = includesTax && cfg?.contract === "dynamic";
  const toAllIn = useMemo(() => {
    if (!showAllIn || !cfg) return (p: number | null) => p;
    const vat = 1 + cfg.vat_pct;
    return (p: number | null) =>
      p == null ? null : (p + cfg.supplier_margin_eur_per_kwh + cfg.tax_eur_per_kwh) * vat;
  }, [showAllIn, cfg]);
  const slots = useMemo(() => {
    if (!data?.slots) return [];
    return data.slots.map((s) => ({
      ...s,
      price_eur_per_kwh: toAllIn(s.price_eur_per_kwh),
    }));
  }, [data, toAllIn]);
  // Bar backdrop for the plan chart: KNOWN prices from /api/pricing/today,
  // extended with the AI-FORECAST prices the plan itself was computed on
  // (ems_plan.price_forecast, ts >= known_price_until). Same all-in
  // transform on both; forecast entries render lighter in the chart.
  const priceCurve = useMemo(() => {
    const dur = resolution === "1h" ? 3600 : 900;
    // Sell side (2026-10-04): on a market-price contract the chart also
    // carries what a kWh to the grid brings in, as a grey line over the
    // buy-price bars (same idiom as the Prijzen chart). Known region
    // from /api/pricing/today, forecast region from the plan's slots.
    const hasSell = pricing?.export_mode === "dynamic" && !!pricing?.export_prices_eur_per_kwh;
    const entries: { t: number; p: number; forecast: boolean; dur: number; sell: number | null }[] = [];
    if (pricing) {
      for (let i = 0; i < pricing.t.length; i++) {
        const p = toAllIn(pricing.prices_eur_per_kwh[i]);
        const sell = hasSell ? (pricing.export_prices_eur_per_kwh?.[i] ?? null) : null;
        if (p != null) entries.push({ t: pricing.t[i], p, forecast: false, dur, sell });
      }
    }
    const ku = data?.ems_plan?.known_price_until ?? null;
    const lastKnown = entries.length ? entries[entries.length - 1].t + dur : null;
    // Forecast region: the engine's 15-min slots. On the 1h view group
    // per hour (the known bars are hourly there). On the 15-min view
    // the AI forecast is per quarter since 2026-09-30 (energyforecast.de, quarter-hourly),
    // so draw the quarters; an hour whose four quarters are identical
    // is an hourly fallback value and draws as one bar instead of a
    // comb of four equal slivers.
    const byBucket = new Map<number, { t: number; p: number; sell: number | null }[]>();
    for (const fp of data?.ems_plan?.price_forecast ?? []) {
      if (ku == null || fp.t < ku) continue;
      if (lastKnown != null && fp.t < lastKnown) continue;
      const p = toAllIn(fp.epex_eur_per_kwh);
      if (p == null) continue;
      const bucket = fp.t - (fp.t % 3600);
      const arr = byBucket.get(bucket) ?? [];
      arr.push({ t: fp.t, p, sell: hasSell ? (fp.export_price_eur_per_kwh ?? null) : null });
      byBucket.set(bucket, arr);
    }
    const meanSell = (pts: { sell: number | null }[]) => {
      const v = pts.map((q) => q.sell).filter((q): q is number => q != null);
      return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
    };
    for (const [bucket, pts] of byBucket) {
      pts.sort((a, b) => a.t - b.t);
      const flat = pts.every((q) => Math.abs(q.p - pts[0].p) < 1e-6);
      if (dur === 3600 || flat) {
        entries.push({
          t: pts[0].t,
          p: pts.reduce((a, q) => a + q.p, 0) / pts.length,
          forecast: true,
          dur: bucket + 3600 - pts[0].t,
          sell: meanSell(pts),
        });
      } else {
        for (const q of pts) entries.push({ t: q.t, p: q.p, forecast: true, dur: 900, sell: q.sell });
      }
    }
    entries.sort((a, b) => a.t - b.t);
    return entries.length ? entries : null;
  }, [pricing, data, toAllIn, resolution]);

  // On the 48u view: does the window actually reach into tomorrow yet?
  // Before the ~13:15 price publish neither plan can extend past
  // midnight - show a hint instead of leaving the user guessing.
  const windowShort = useMemo(() => {
    if (!data) return false;
    let end = 0;
    for (const s of data.slots) {
      end = Math.max(end, new Date(s.start).getTime() / 1000 + s.duration_sec);
    }
    for (const p of data.ems_plan?.trajectory ?? []) {
      end = Math.max(end, p.t);
    }
    return end > 0 && end - Date.now() / 1000 < 26 * 3600;
  }, [data]);

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-5 lg:max-w-4xl lg:px-8 lg:pt-10 lg:pb-12">
      <ScreenHeader title="Prognose" subtitle="Wat je batterij van plan is" />

      {error && (
        <div className="text-sm text-danger bg-danger-soft rounded-2xl p-3 border border-card-border">
          {error}
        </div>
      )}

      {data == null ? (
        <>
          <Skeleton className="h-24" />
          <Skeleton className="h-64" />
        </>
      ) : !data.available ? (
        <Card className="p-6 text-center flex flex-col items-center gap-2">
          <span className="w-12 h-12 rounded-full bg-card-soft grid place-items-center text-ink-mute">
            <CrystalIcon />
          </span>
          <div className="font-semibold">Geen planning beschikbaar</div>
          <p className="text-sm text-ink-mute max-w-xs">
            Er is nog geen laadplanning voor jouw systeem ontvangen.
            Vraag je installateur ernaar.
          </p>
        </Card>
      ) : (
        <>
          <NowCard slots={slots} live={live} />
          {data.ems_plan?.balancing?.window_start != null && (
            <BalancingCard bal={data.ems_plan.balancing} />
          )}
          <SmartMomentsCard />
          {data.ems_plan && <PlanCostCard ems={data.ems_plan} />}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <SectionTitle>Batterijplan &amp; stroomprijs</SectionTitle>
              <HorizonToggle value={horizon} onChange={setHorizon} />
            </div>
            <Card className="p-4">
              <ForecastChart
                slots={slots}
                liveSoc={live?.soc ?? null}
                ems={data.ems_plan}
                prices={priceCurve}
                horizonHours={horizon}
              />
              <Legend
                hasForecastPrices={(data.ems_plan?.price_forecast?.length ?? 0) > 0}
                hasSellPrices={priceCurve?.some((e) => e.sell != null) ?? false}
              />
            </Card>
            {horizon >= 48 && windowShort && (
              <p className="text-xs text-ink-mute leading-relaxed px-1">
                Morgen verschijnt hier zodra de dagprijzen zijn gepubliceerd
                (meestal rond 13:15) en het systeem zijn plan heeft verlengd.
              </p>
            )}
            <p className="text-xs text-ink-mute leading-relaxed px-1">
              {showAllIn
                ? "Prijzen inclusief leveringsmarge en energiebelasting (totaalprijs uit je contract). Schakel om in Account → Energiecontract."
                : "Inkoopprijs op de groothandelsmarkt (EPEX), exclusief leveringsmarge en energiebelasting. Inclusief tonen? Zet aan in Account → Energiecontract."}
            </p>
          </section>
          {(data.ems_plan?.loads?.length ?? 0) > 0 ? (
            <section className="flex flex-col gap-3">
              <SectionTitle>Verwachte energiebalans</SectionTitle>
              <Card className="p-4">
                <BalanceChart loads={data.ems_plan!.loads} />
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-mute mt-3">
                  <LegendSwatch color="var(--color-sun)" label="zon" />
                  <LegendSwatch color="var(--color-home)" label="huis (incl. warmtepomp)" />
                  <LegendSwatch color="var(--color-ev)" label="auto laden" />
                  <LegendSwatch color="var(--color-accent)" label="batterij" />
                  <LegendSwatch color="var(--color-grid)" label="net" />
                </div>
              </Card>
              <p className="text-xs text-ink-mute leading-relaxed px-1">
                Boven de lijn waar de stroom per uur vandaan komt (zon,
                batterij, net), eronder waar hij naartoe gaat (huis, auto,
                batterij, net). Beide kanten zijn per uur even groot.
              </p>
            </section>
          ) : (data.solar_forecast || data.consumption_forecast) && (
            <section className="flex flex-col gap-3">
              <SectionTitle>Zon &amp; verbruik - komende 48 uur</SectionTitle>
              <Card className="p-4">
                <OutlookChart solar={data.solar_forecast} consumption={data.consumption_forecast} />
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-mute mt-3">
                  <LegendSwatch color="var(--color-sun)" label="zonverwachting" />
                  <LegendSwatch color="var(--color-home)" label="verbruiksverwachting" />
                </div>
              </Card>
            </section>
          )}
          <UpcomingList slots={slots} liveSoc={live?.soc ?? null}
            max={horizon >= 48 ? 10 : 6} />
          <PlanHistorySection />
        </>
      )}
    </div>
  );
}

/* ---------------- terugblik: plan vs werkelijkheid ---------------- */

function localDayString(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function PlanHistorySection() {
  // Default gisteren: een afgeronde dag vertelt het hele verhaal;
  // vandaag is halverwege interessanter in de prognose zelf.
  const [day, setDay] = useState<string>(() => localDayString(1));
  const [hist, setHist] = useState<PlanHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchPlanHistory(day)
      .then((h) => { if (!cancelled) setHist(h); })
      .catch(() => { if (!cancelled) setErr("Terugblik laden mislukte."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [day]);

  const today = localDayString(0);
  const chips: { label: string; value: string }[] = [
    { label: "Vandaag", value: today },
    { label: "Gisteren", value: localDayString(1) },
    { label: "Eergisteren", value: localDayString(2) },
  ];
  const chipDays = chips.map((c) => c.value);

  const hasPlan = hist?.planned_soc_pct.some((v) => v != null) ?? false;
  const hasActual = hist?.actual_soc_pct.some((v) => v != null) ?? false;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <SectionTitle>Terugblik</SectionTitle>
        <div className="flex items-center gap-1.5">
          <div className="flex rounded-full bg-card-soft border border-card-border p-0.5 text-xs font-medium">
            {chips.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setDay(c.value)}
                className={`px-2.5 py-1 rounded-full transition-colors ${
                  day === c.value ? "bg-card text-ink shadow-sm" : "text-ink-mute"
                }`}
                aria-pressed={day === c.value}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Vrije datumkeuze voor alles ouder dan de chips - native
              picker, data blijft een jaar beschikbaar. */}
          <input
            type="date"
            value={chipDays.includes(day) ? "" : day}
            max={today}
            min={localDayString(365)}
            onChange={(e) => { if (e.target.value) setDay(e.target.value); }}
            aria-label="Kies een dag"
            className="rounded-full bg-card-soft border border-card-border px-2.5 py-1 text-xs text-ink-mute w-[7.5rem]"
          />
        </div>
      </div>
      {loading || hist == null ? (
        <Skeleton className="h-56" />
      ) : err ? (
        <div className="text-sm text-danger bg-danger-soft rounded-2xl p-3 border border-card-border">{err}</div>
      ) : !hasPlan && !hasActual ? (
        <Card className="p-6 text-center text-sm text-ink-mute">
          Geen gegevens opgeslagen voor deze dag.
        </Card>
      ) : (
        <Card className="p-4">
          <HistoryChart hist={hist} />
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-mute mt-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 border-t-2 border-dashed border-accent" /> gepland verloop
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 border-t-2 border-ink" /> werkelijk verloop
            </span>
            <LegendSwatch color="var(--color-grid)" label="stroomprijs" />
          </div>
          {!hasPlan && (
            <p className="text-xs text-ink-mute mt-2">
              Voor deze dag is geen plan opgeslagen; alleen het gemeten
              verloop wordt getoond.
            </p>
          )}
        </Card>
      )}
    </section>
  );
}

function HistoryChart({ hist }: { hist: PlanHistory }) {
  // Scrub - zelfde idioom als ForecastChart/PairChart: CTM vertaalt
  // clientX naar kwartier-index, touch krijgt een 3 ms tikje per snap.
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibratedIdx = useRef<number | null>(null);

  const t0 = hist.t[0];
  const tEnd = hist.t[hist.t.length - 1] + 900;
  const span = Math.max(1, tEnd - t0);
  const x = (ts: number) => PAD.left + ((ts - t0) / span) * (VB_W - PAD.left - PAD.right);
  const chartH = VB_H - PAD.top - PAD.bottom;
  const ySoc = (pct: number) => PAD.top + (1 - pct / 100) * chartH;

  function pointerToIndex(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const cl = Math.max(PAD.left, Math.min(VB_W - PAD.right, vbX));
    const frac = (cl - PAD.left) / (VB_W - PAD.left - PAD.right);
    return Math.min(hist.t.length - 1, Math.max(0, Math.floor(frac * hist.t.length)));
  }
  function setFromClient(clientX: number, isTouch: boolean) {
    const idx = pointerToIndex(clientX);
    if (idx === null) return;
    setHoverIdx(idx);
    if (isTouch && idx !== lastVibratedIdx.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibratedIdx.current = idx;
    }
  }
  function clearScrub() {
    setHoverIdx(null);
    lastVibratedIdx.current = null;
  }

  // Prijsbalken - zelfde kwartiel-tinten als de prognosegrafiek.
  const priceVals = hist.prices_eur_per_kwh.filter((v): v is number => v != null);
  const [pMin, pMax] = priceAxis(priceVals);
  const sorted = [...priceVals].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)] ?? 0;
  const q3 = sorted[Math.floor(sorted.length * 0.75)] ?? 1;
  const tierFill = (v: number) =>
    v <= q1 ? "var(--color-price-low)" : v >= q3 ? "var(--color-price-high)" : "var(--color-grid)";
  const priceH = chartH * 0.6;
  const yPrice = (v: number) =>
    VB_H - PAD.bottom - ((v - pMin) / (pMax - pMin || 1)) * priceH;
  const barW = Math.max(1, (900 / span) * (VB_W - PAD.left - PAD.right) - 1);

  // SOC-lijnen: onderbreek bij gaten (null) i.p.v. erdoorheen te trekken.
  const linePath = (vals: (number | null)[]) => {
    let d = "";
    let pen = false;
    hist.t.forEach((ts, i) => {
      const v = vals[i];
      if (v == null) { pen = false; return; }
      const px = x(ts).toFixed(1);
      const py = ySoc(v).toFixed(1);
      d += pen ? ` L ${px} ${py}` : `${d ? " " : ""}M ${px} ${py}`;
      pen = true;
    });
    return d;
  };
  const plannedD = linePath(hist.planned_soc_pct);
  const actualD = linePath(hist.actual_soc_pct);

  const ticks: number[] = [];
  for (let ts = Math.ceil(t0 / 10800) * 10800; ts < tEnd; ts += 10800) ticks.push(ts);

  return (
    <>
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="w-full h-auto select-none"
      role="img"
      aria-label="Terugblik: gepland versus werkelijk ladingsverloop"
      style={{ touchAction: "pan-y" }}
      data-swipe-consume="true"
      onMouseMove={(e) => setFromClient(e.clientX, false)}
      onMouseLeave={clearScrub}
      onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
      onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
      onTouchEnd={clearScrub}
      onTouchCancel={clearScrub}
    >
      {[0, 50, 100].map((pct) => (
        <g key={pct}>
          <line x1={PAD.left} x2={VB_W - PAD.right} y1={ySoc(pct)} y2={ySoc(pct)}
                stroke="var(--color-card-border)" strokeWidth={1}
                strokeDasharray={pct === 0 ? undefined : "3 4"} />
          <text x={PAD.left - 5} y={ySoc(pct) + 3.5} textAnchor="end" fontSize={10}
                fill="var(--color-ink-mute)">{pct}%</text>
        </g>
      ))}
      {hist.prices_eur_per_kwh.map((v, i) =>
        v == null ? null : (
          <rect key={i} x={x(hist.t[i])} y={yPrice(v)} width={barW}
                height={Math.max(3, VB_H - PAD.bottom - yPrice(v))}
                fill={tierFill(v)} opacity={0.35} rx={1} />
        ),
      )}
      {plannedD && (
        <path d={plannedD} fill="none" stroke="var(--color-accent)" strokeWidth={2}
              strokeDasharray="5 4" strokeLinejoin="round" />
      )}
      {actualD && (
        <path d={actualD} fill="none" stroke="var(--color-ink)" strokeWidth={2}
              strokeLinejoin="round" opacity={0.85} />
      )}
      {ticks.map((ts) => (
        <text key={ts} x={x(ts)} y={VB_H - PAD.bottom + 16} textAnchor="middle"
              fontSize={10} fill="var(--color-ink-mute)">
          {new Date(ts * 1000).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
        </text>
      ))}
      {hoverIdx != null && (
        <line
          x1={x(hist.t[hoverIdx] + 450)} x2={x(hist.t[hoverIdx] + 450)}
          y1={PAD.top} y2={VB_H - PAD.bottom}
          stroke="var(--color-ink-mute)" strokeWidth={1} strokeDasharray="3 3"
        />
      )}
    </svg>
    {hoverIdx != null && (
      <div className="text-xs text-ink-mute px-1 mt-1 flex justify-between gap-2 flex-wrap">
        <span>
          {new Date(hist.t[hoverIdx] * 1000).toLocaleTimeString("nl-NL",
            { hour: "2-digit", minute: "2-digit" })}
        </span>
        <span className="flex gap-3">
          <span className="text-accent">
            gepland {hist.planned_soc_pct[hoverIdx] != null
              ? `${Math.round(hist.planned_soc_pct[hoverIdx]!)}%` : "-"}
          </span>
          <span>
            werkelijk {hist.actual_soc_pct[hoverIdx] != null
              ? `${Math.round(hist.actual_soc_pct[hoverIdx]!)}%` : "-"}
          </span>
          <span>
            {hist.prices_eur_per_kwh[hoverIdx] != null
              ? `${(hist.prices_eur_per_kwh[hoverIdx]! * 100).toFixed(1).replace(".", ",")} ct`
              : "- ct"}
          </span>
        </span>
      </div>
    )}
    </>
  );
}

/* ---------------- horizon toggle (24u / 48u / 72u) ---------------- */

function HorizonToggle({
  value,
  onChange,
}: {
  value: 24 | 48 | 72;
  onChange: (v: 24 | 48 | 72) => void;
}) {
  const opts: (24 | 48 | 72)[] = [24, 48, 72];
  return (
    <div className="flex rounded-full bg-card-soft border border-card-border p-0.5 text-xs font-medium">
      {opts.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => onChange(h)}
          className={`px-3 py-1 rounded-full transition-colors ${
            value === h ? "bg-card text-ink shadow-sm" : "text-ink-mute"
          }`}
          aria-pressed={value === h}
        >
          {h} uur
        </button>
      ))}
    </div>
  );
}

/* ---------------- balanceerbeurt ---------------- */

// Waarom de batterij straks twee uur vol blijft staan terwijl het plan
// anders zou ontladen: de balanceerbeurt (engine balancing.py). Alleen
// getoond zolang het plan er een bevat; de datum van de vorige keer
// is gemeten, niet gepland.
function BalancingCard({ bal }: { bal: EmsBalancing }) {
  if (bal.window_start == null || bal.window_end == null) return null;
  const startIso = new Date(bal.window_start * 1000).toISOString();
  const endIso = new Date(bal.window_end * 1000).toISOString();
  const today = new Date().toDateString();
  const startDay = new Date(bal.window_start * 1000).toDateString();
  const dayWord = startDay === today ? "vandaag"
    : new Date(bal.window_start * 1000).toLocaleDateString("nl-NL", { weekday: "long" });
  const last = bal.last_balanced_at
    ? new Date(bal.last_balanced_at * 1000).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })
    : null;
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Pill tone="accent">Balanceren</Pill>
        <div className="text-sm font-medium">
          {dayWord} {clock(startIso)} - {clock(endIso)} blijft de batterij op {Math.round(bal.soc_pct)}-100%
        </div>
      </div>
      <p className="text-xs text-ink-mute mt-2">
        Eens per {bal.interval_days} dagen houdt het systeem de batterij {Math.round(bal.hold_hours)} uur
        vol, zodat het batterijbeheer alle cellen gelijk kan trekken. Dat houdt de capaciteit en de
        levensduur op peil. In die uren wordt er niet ontladen, ook niet als de stroomprijs hoog is.
        {last ? ` De vorige keer vol was ${last}.` : " Sinds de installatie is de batterij nog niet twee uur vol geweest."}
        {" "}Staat de batterij vanzelf twee uur vol, bijvoorbeeld door de zon, dan telt dat ook en schuift de volgende beurt op.
      </p>
    </Card>
  );
}

/* ---------------- "nu" summary ---------------- */

const ACTION_LABEL: Record<string, string> = {
  laden: "Laden",
  ontladen: "Ontladen",
  rust: "Rust",
};
const ACTION_TONE: Record<string, "accent" | "home" | "mute"> = {
  laden: "accent",
  ontladen: "home",
  rust: "mute",
};

function NowCard({ slots, live }: { slots: ForecastSlot[]; live: Live | null }) {
  if (slots.length === 0) return null;
  const cur = slots[0];
  // WHAT THE BATTERY IS DOING = live power measurement - the plan's
  // target deltas can read "rust" while the battery discharges at full
  // power to CATCH UP with a target below the current SOC.
  const state = live ? batteryStateLabel(live) : null;
  const exporting = live != null && live.grid_w < -100;
  // Where the plan is heading: the last slot before the action changes.
  const change = slots.find((s) => s.action !== cur.action);
  const runEndIdx = change ? slots.indexOf(change) : slots.length - 1;
  const goal = slots[Math.max(0, runEndIdx - 1)] ?? cur;
  // target_soc_pct is de EINDstand van het slot - het doel is dus rond
  // het einde van dat slot bereikt, niet rond het begin.
  const goalEnd = new Date(new Date(goal.start).getTime() + goal.duration_sec * 1000).toISOString();
  const goalText = `plan: naar ${Math.round(goal.target_soc_pct)}% rond ${clock(goalEnd)}`;
  return (
    <Card className="p-4 flex items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-ink-mute">Nu</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {state ? (
            <Pill tone={state.tone === "grid" ? "grid" : state.tone === "sun" ? "sun" : state.tone === "mute" ? "mute" : "accent"}>
              {state.label}
              {state.hint ? ` · ${state.hint}` : ""}
            </Pill>
          ) : (
            <Pill tone={ACTION_TONE[cur.action]}>{ACTION_LABEL[cur.action]}</Pill>
          )}
          {exporting && <Pill tone="sun">Teruglevering</Pill>}
          {cur.price_eur_per_kwh != null && (
            <span className="text-xs text-ink-mute tabular">
              {formatPricePerKwh(cur.price_eur_per_kwh)}
            </span>
          )}
        </div>
        <div className="text-xs text-ink-mute mt-2">
          {goalText}
          {change && ` · ${clock(change.start)} → ${ACTION_LABEL[change.action].toLowerCase()}`}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="display-num text-3xl text-accent">
          {live != null ? Math.round(live.soc) : "-"}
          <span className="text-base text-ink-mute font-semibold">%</span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-ink-mute">batterij nu</div>
      </div>
    </Card>
  );
}

/* ---------------- kosten van het plan ---------------- */

function euro(v: number): string {
  return `€${v.toFixed(2).replace(".", ",")}`;
}

/** Wat het plan naar verwachting kost, en wat het scheelt t.o.v. de
 *  accu niets laten doen. Merkonafhankelijk: hier stond tot 14-08 een
 *  vergelijking met Victron DESS uit de schaduwproef ("Victron blijft
 *  sturen; dit is een proef"). Die proef is voorbij - we sturen de
 *  omvormers zelf aan - en lang niet elke klant heeft Victron, dus een
 *  Victron-vergelijking was voor hen ronduit verwarrend. */
function PlanCostCard({ ems }: { ems: EmsPlan }) {
  const ours = ems.projected_ems_eur;
  const base = ems.projected_baseline_eur;
  if (ours == null) return null;
  const saved = base != null ? base - ours : null;
  return (
    <Card className="p-4 flex items-center gap-4">
      <span
        className="w-9 h-9 rounded-full grid place-items-center shrink-0"
        style={{ background: "color-mix(in srgb, var(--color-price-low) 18%, transparent)" }}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
          stroke="var(--color-price-low)" strokeWidth={2} strokeLinecap="round"
          strokeLinejoin="round" aria-hidden>
          <path d="M13 3 5 13h5l-1 8 8-10h-5l1-8Z" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-ink-mute">SlimStroom plan</div>
        <div className="text-sm mt-0.5">
          Verwachte kosten:{" "}
          <span className="font-semibold tabular">{euro(ours)}</span>
        </div>
        <div className="text-xs text-ink-mute mt-1">
          {saved != null && saved > 0.005
            ? `${euro(saved)} minder dan wanneer de accu niets zou doen.`
            : saved != null && saved < -0.005
              ? "Deze periode levert slim sturen weinig op - de prijzen liggen te dicht bij elkaar."
              : "Slim laden en ontladen op de goedkoopste momenten."}
        </div>
      </div>
    </Card>
  );
}

/* ---------------- plan chart ---------------- */

const VB_W = 700;
const VB_H = 240;
const PAD = { left: 34, right: 12, top: 12, bottom: 34 };

/** Prijs-as op een 5ct-raster rond de ECHTE spreiding - as vanaf 0
 *  drukte alle variatie plat zodra er een piek in de dag zat. Minimaal
 *  5ct spanwijdte zodat een vlakke dag geen ruisversterker wordt;
 *  negatieve prijzen verlagen de vloer gewoon mee. */
function priceAxis(vals: number[]): [number, number] {
  if (!vals.length) return [0, 1];
  const maxCt = Math.max(...vals) * 100;
  const minCt = Math.min(...vals) * 100;
  const top = Math.ceil(maxCt / 5) * 5;
  let bottom = Math.floor(minCt / 5) * 5;
  if (top - bottom < 5) bottom = top - 5;
  return [bottom / 100, top / 100];
}

function ForecastChart({
  slots,
  liveSoc,
  ems,
  prices,
  horizonHours,
}: {
  slots: ForecastSlot[];
  liveSoc: number | null;
  ems?: EmsPlan | null;
  prices?: { t: number; p: number; forecast: boolean; dur: number; sell?: number | null }[] | null;
  horizonHours: 24 | 48 | 72;
}) {
  // Scrub state - same idiom as PriceChart (CTM maps clientX -> time,
  // touch scrub gets a 3 ms haptic tick per snapped kwartier).
  const [hoverTs, setHoverTs] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibrated = useRef<number | null>(null);
  if (slots.length === 0) return null;

  const t0 = new Date(slots[0].start).getTime() / 1000;
  const last = slots[slots.length - 1];
  const dessEnd = new Date(last.start).getTime() / 1000 + last.duration_sec;
  // Het venster dekt de hele planning: onze reeks loopt vaak verder
  // further than the DESS schedule (the engine plans to the end of the
  // known prices; een DESS-ring wordt later op de middag verlengd).
  // 24/48-uur-schakelaar: kap het venster op t0 + horizon. Op
  // engine-gestuurde sites levert de API altijd het volle 48u-plan,
  // dus zonder deze clip deed de schakelaar niets (Stefan 19-08).
  const cap = t0 + horizonHours * 3600;
  const emsPts = (ems?.trajectory ?? []).filter((p) => p.t >= t0 && p.t <= cap);
  const emsEnd = emsPts.length ? emsPts[emsPts.length - 1].t : t0;
  const t1 = Math.min(Math.max(dessEnd, emsEnd), cap);
  const span = Math.max(1, t1 - t0);
  const x = (ts: number) => PAD.left + ((ts - t0) / span) * (VB_W - PAD.left - PAD.right);

  const chartH = VB_H - PAD.top - PAD.bottom;
  const ySoc = (pct: number) => PAD.top + (1 - pct / 100) * chartH;

  // Price bars: prefer the merged curve from the parent - known prices
  // plus the AI-forecast region (drawn lighter). Fall back to slot
  // prices when the curve isn't loaded (first paint).
  const bars: { ts: number; price: number; forecast: boolean; dur: number; sell: number | null }[] = [];
  if (prices) {
    for (const e of prices) {
      if (e.t + e.dur > t0 && e.t < t1) {
        bars.push({ ts: e.t, price: e.p, forecast: e.forecast, dur: e.dur, sell: e.sell ?? null });
      }
    }
  }
  if (!bars.length) {
    for (const s of slots) {
      if (s.price_eur_per_kwh != null) {
        bars.push({
          ts: new Date(s.start).getTime() / 1000,
          price: s.price_eur_per_kwh,
          forecast: false,
          dur: s.duration_sec,
          sell: null,
        });
      }
    }
  }
  const forecastFrom = bars.find((b) => b.forecast)?.ts ?? null;
  const barVals = bars.map((b) => b.price);
  const sellVals = bars.map((b) => b.sell).filter((v): v is number => v != null);
  // Niet-nul-as op een 5ct-raster (zelfde besluit als de Prijzen-
  // grafiek): met de as vanaf 0 oogde een 86ct-piek als half-hoog en
  // de gewone 20-25ct-balken als bodemprijzen (Stefan, 10-09). Het
  // prijsvak kreeg ook meer hoogte (45% -> 60%).
  const [pMin, pMax] = priceAxis([...barVals, ...sellVals]);
  const sorted = [...barVals].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)] ?? 0;
  const q3 = sorted[Math.floor(sorted.length * 0.75)] ?? 1;
  const tierFill = (p: number) =>
    p <= q1 ? "var(--color-price-low)" : p >= q3 ? "var(--color-price-high)" : "var(--color-grid)";
  const priceH = chartH * 0.6;
  const yPrice = (p: number) =>
    VB_H - PAD.bottom - ((p - pMin) / (pMax - pMin || 1)) * priceH;
  const barWFor = (dur: number) =>
    Math.max(1, (dur / span) * (VB_W - PAD.left - PAD.right) - 1);

  // Plan stepline: the TargetSoc glide, point at each slot start -
  // begrensd tot het gekozen venster.
  const pts = slots
    .map((s) => ({
      ts: new Date(s.start).getTime() / 1000,
      soc: s.target_soc_pct,
      action: s.action,
    }))
    .filter((p) => p.ts <= t1);
  let d = "";
  pts.forEach((p, i) => {
    const px = x(p.ts).toFixed(1);
    const py = ySoc(p.soc).toFixed(1);
    if (i === 0) d = `M ${px} ${py}`;
    else d += ` L ${px} ${py}`;
  });
  d += ` L ${x(Math.min(dessEnd, t1)).toFixed(1)} ${ySoc(pts[pts.length - 1].soc).toFixed(1)}`;

  const now = Date.now() / 1000;
  const nowX = now >= t0 && now <= t1 ? x(now) : null;

  // Hour ticks - every 3 hours; every 6 on the wide (48u) view so the
  // labels don't collide. Midnights additionally get a weekday label +
  // separator line (same idiom as the Zon & verbruik outlook).
  const tickStep = span > 30 * 3600 ? 21600 : 10800;
  const ticks: number[] = [];
  const firstHour = Math.ceil(t0 / tickStep) * tickStep;
  for (let ts = firstHour; ts < t1; ts += tickStep) ticks.push(ts);
  const midnights: { ts: number; label: string }[] = [];
  for (let ts = Math.ceil(t0 / 3600) * 3600; ts < t1; ts += 3600) {
    const dd = new Date(ts * 1000);
    if (dd.getHours() === 0 && dd.getMinutes() === 0) {
      midnights.push({
        ts,
        label: dd.toLocaleDateString("nl-NL", { weekday: "short" }),
      });
    }
  }

  // ---- scrub helpers --------------------------------------------------
  function pointerToTs(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const inner = VB_W - PAD.left - PAD.right;
    const frac = Math.max(0, Math.min(1, (vbX - PAD.left) / inner));
    // Snap to the kwartier grid the data lives on.
    return Math.round((t0 + frac * span) / 900) * 900;
  }
  function setFromClient(clientX: number, isTouch: boolean) {
    const ts = pointerToTs(clientX);
    if (ts == null) return;
    setHoverTs(ts);
    if (isTouch && ts !== lastVibrated.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibrated.current = ts;
    }
  }
  function clearScrub() {
    setHoverTs(null);
    lastVibrated.current = null;
  }

  // Values at the focused moment (hover, else "nu").
  const focusTs = hoverTs ?? Math.min(t1, Math.max(t0, Math.round(now / 900) * 900));
  const focusSlot = slots.find((s) => {
    const st = new Date(s.start).getTime() / 1000;
    return st <= focusTs && focusTs < st + s.duration_sec;
  });
  const focusBar = bars.find((b) => b.ts <= focusTs && focusTs < b.ts + b.dur);
  const focusX = x(Math.max(t0, Math.min(t1, focusTs)));
  const focusIsToday =
    new Date(focusTs * 1000).toDateString() === new Date().toDateString();

  return (
    <div className="select-none">
      {/* readout - stable height so scrubbing doesn't shift layout */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs mb-1 min-h-[1.25rem]">
        <span className="font-semibold tabular text-ink">
          {!focusIsToday && (
            <span className="text-ink-mute font-normal">
              {new Date(focusTs * 1000).toLocaleDateString("nl-NL", { weekday: "short" })}{" "}
            </span>
          )}
          {clock(new Date(focusTs * 1000).toISOString())}
        </span>
        {focusSlot && (
          <span className="text-ink-mute">
            Accu{" "}
            <span className="text-ink font-medium tabular">
              {Math.round(focusSlot.target_soc_pct)}%
            </span>
          </span>
        )}
        {focusBar && (
          <span className="text-ink-mute tabular">
            {focusBar.forecast ? "~" : ""}
            {formatPricePerKwh(focusBar.price)}
            {focusBar.sell != null && (
              <> · terugleveren {formatPricePerKwh(focusBar.sell)}</>
            )}
          </span>
        )}
        {hoverTs == null && <span className="text-ink-mute">· nu</span>}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Batterijplan en stroomprijs"
        data-swipe-consume="true"
        style={{ touchAction: "pan-y" }}
        onMouseMove={(e) => setFromClient(e.clientX, false)}
        onMouseLeave={clearScrub}
        onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchEnd={clearScrub}
        onTouchCancel={clearScrub}
      >
        {/* SOC gridlines 0/50/100 */}
        {[0, 50, 100].map((pct) => (
          <g key={pct}>
            <line x1={PAD.left} x2={VB_W - PAD.right} y1={ySoc(pct)} y2={ySoc(pct)}
              stroke="var(--color-line)" strokeWidth={1} strokeDasharray={pct === 0 ? "" : "2 4"} />
            <text x={PAD.left - 6} y={ySoc(pct)} textAnchor="end" dominantBaseline="central"
              fontSize={10} fill="var(--color-ink-mute)" className="tabular">
              {pct}%
            </text>
          </g>
        ))}

        {/* price bars along the bottom - full window, incl. beyond the
            schema waar alleen de prijsreeks nog doorloopt. The
            AI-forecast region (energyforecast.de) renders lighter. */}
        {bars.map((b) => (
          <rect
            key={`p${b.ts}`}
            x={x(b.ts) + 0.5}
            y={yPrice(b.price)}
            width={barWFor(b.dur)}
            height={Math.max(3, VB_H - PAD.bottom - yPrice(b.price))}
            rx={1.5}
            fill={tierFill(b.price)}
            opacity={b.forecast ? 0.22 : 0.35}
          />
        ))}

        {/* sell price: grey stepped line over the bars (market-price contract) */}
        {sellVals.length > 0 && (
          <path
            d={bars
              .filter((b) => b.sell != null)
              .map((b) => {
                const xa = Math.max(PAD.left, x(b.ts));
                const xb = Math.min(VB_W - PAD.right, x(b.ts + b.dur));
                const yy = yPrice(b.sell as number);
                return `M${xa.toFixed(1)} ${yy.toFixed(1)} L${xb.toFixed(1)} ${yy.toFixed(1)}`;
              })
              .join(" ")}
            fill="none"
            stroke="var(--color-ink-mute)"
            strokeWidth={1.4}
            strokeOpacity={0.6}
            strokeLinecap="round"
          />
        )}

        {/* boundary: known prices end, AI price forecast begins */}
        {forecastFrom != null && forecastFrom > t0 && (
          <g>
            <line x1={x(forecastFrom)} x2={x(forecastFrom)} y1={PAD.top}
              y2={VB_H - PAD.bottom} stroke="var(--color-price-high)"
              strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />
            <text x={x(forecastFrom) + 4} y={PAD.top + 22} fontSize={8.5}
              fill="var(--color-ink-mute)">
              verwachting →
            </text>
          </g>
        )}

        {/* action bands under the SOC line */}
        {slots.map((s) =>
          s.action === "rust" ? null : (
            <rect
              key={`a${s.start}`}
              x={x(new Date(s.start).getTime() / 1000)}
              y={PAD.top}
              width={(s.duration_sec / span) * (VB_W - PAD.left - PAD.right)}
              height={chartH}
              fill={s.action === "laden" ? "var(--color-accent)" : "var(--color-home)"}
              opacity={0.08}
            />
          ),
        )}

        {/* Tot 14-08 lag hier een gestippelde tweede lijn: ons plan
            naast dat van Victron. Nu wij zelf sturen is de
            doorgetrokken lijn ONS plan - twee lijnen zouden hetzelfde
            tonen. */}

        {/* predicted SOC stepline */}
        <path d={d} fill="none" stroke="var(--color-accent)" strokeWidth={2.5}
          strokeLinejoin="round" strokeLinecap="round" />

        {/* now marker + LIVE SOC dot (where the battery actually is,
            vs the plan line it is chasing) */}
        {nowX != null && (
          <g>
            <line x1={nowX} x2={nowX} y1={PAD.top - 4} y2={VB_H - PAD.bottom}
              stroke="var(--color-ink)" strokeWidth={1.2} strokeDasharray="3 3" opacity={0.6} />
            <text x={nowX} y={PAD.top - 2} textAnchor="middle" fontSize={9}
              fontWeight={700} fill="var(--color-ink-mute)">nu</text>
            {liveSoc != null && (
              <g>
                <circle cx={nowX} cy={ySoc(liveSoc)} r={7} fill="var(--color-accent)" opacity={0.25} />
                <circle cx={nowX} cy={ySoc(liveSoc)} r={3.5} fill="var(--color-accent)"
                  stroke="var(--color-card)" strokeWidth={1.5} />
                <text x={nowX + 10} y={ySoc(liveSoc)} dominantBaseline="central" fontSize={10}
                  fontWeight={700} fill="var(--color-accent)" className="tabular">
                  {`${Math.round(liveSoc)}%`}
                </text>
              </g>
            )}
          </g>
        )}

        {/* scrub line at the focused moment */}
        {hoverTs != null && (
          <line x1={focusX} x2={focusX} y1={PAD.top} y2={VB_H - PAD.bottom}
            stroke="var(--color-ink-mute)" strokeWidth={1.2} opacity={0.8} />
        )}

        {/* price scale (right edge, in cents) */}
        {bars.length > 0 && (
          <>
            <text x={VB_W - PAD.right} y={yPrice(pMax) - 4} textAnchor="end" fontSize={9}
              fill="var(--color-ink-mute)" className="tabular">
              {`${Math.round(pMax * 100)}\u00a2`}
            </text>
            <text x={VB_W - PAD.right} y={VB_H - PAD.bottom - 3} textAnchor="end" fontSize={9}
              fill="var(--color-ink-mute)" className="tabular">
              {`${Math.round(pMin * 100)}\u00a2`}
            </text>
          </>
        )}

        {/* x-axis hour labels */}
        {ticks.map((ts) => (
          <text key={ts} x={x(ts)} y={VB_H - PAD.bottom + 16} textAnchor="middle"
            fontSize={10} fill="var(--color-ink-mute)" className="tabular">
            {clock(new Date(ts * 1000).toISOString())}
          </text>
        ))}

        {/* midnight separators + day labels (only meaningful on 48u) */}
        {midnights.map((m) => (
          <g key={`d${m.ts}`}>
            <line x1={x(m.ts)} x2={x(m.ts)} y1={PAD.top} y2={VB_H - PAD.bottom}
              stroke="var(--color-line)" strokeWidth={1.5} />
            <text x={x(m.ts) + 4} y={PAD.top + 10} fontSize={9} fontWeight={600}
              fill="var(--color-ink-mute)">
              {m.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function Legend({
  hasForecastPrices = false,
  hasSellPrices = false,
}: {
  hasForecastPrices?: boolean;
  hasSellPrices?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-mute mt-3">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-0.5 rounded-full bg-accent" /> Gepland ladingsverloop
      </span>
      {hasForecastPrices && (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-3 rounded-sm"
            style={{ background: "var(--color-grid)", opacity: 0.2 }}
          />
          lichter = AI-prijsverwachting
        </span>
      )}
      {hasSellPrices && (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 rounded-full bg-ink-mute opacity-60" />
          grijze lijn = terugleverprijs
        </span>
      )}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2 h-3 rounded-sm" style={{ background: "var(--color-price-low)", opacity: 0.5 }} />
        <span className="inline-block w-2 h-3 rounded-sm -ml-1" style={{ background: "var(--color-grid)", opacity: 0.45 }} />
        <span className="inline-block w-2 h-3 rounded-sm -ml-1" style={{ background: "var(--color-price-high)", opacity: 0.5 }} />
        stroomprijs (groen = goedkoop, oranje = duur)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "var(--color-accent)", opacity: 0.35 }} /> laden-venster
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "var(--color-home)", opacity: 0.35 }} /> ontladen-venster
      </span>
    </div>
  );
}

/* ---------------- upcoming actions ---------------- */

interface Run {
  action: string;
  start: string;
  end: number;         // unix seconds
  fromSoc: number;
  toSoc: number;
  avgPrice: number | null;
  feedIn: boolean;
}

/** Collapse consecutive same-action slots into human-scale runs.
 *
 * SOC-semantiek (sinds de kwartier-fix van 17-08): `target_soc_pct` is
 * de SOC aan het EIND van het slot. Een run loopt dus van het doel van
 * het slot ERVOOR naar het doel van zijn EIGEN laatste slot - voor de
 * eerste run is dat vertrekpunt de GEMETEN SOC. De oude vorm keek een
 * slot vooruit en toonde "7% -> 7%": het einddoel van de run als
 * vertrekpunt, met het doel van het rust-slot erna als bestemming. */
function toRuns(slots: ForecastSlot[], liveSoc: number | null = null): Run[] {
  const runs: Run[] = [];
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    const ts = new Date(s.start).getTime() / 1000;
    const from = i > 0 ? slots[i - 1].target_soc_pct
      : liveSoc != null ? liveSoc
        : s.target_soc_pct;
    const prev = runs[runs.length - 1];
    if (prev && prev.action === s.action) {
      prev.end = ts + s.duration_sec;
      prev.toSoc = s.target_soc_pct;
      if (s.price_eur_per_kwh != null) {
        prev.avgPrice = prev.avgPrice == null
          ? s.price_eur_per_kwh
          : (prev.avgPrice + s.price_eur_per_kwh) / 2;
      }
      prev.feedIn = prev.feedIn || s.allow_grid_feed_in === true;
    } else {
      runs.push({
        action: s.action,
        start: s.start,
        end: ts + s.duration_sec,
        fromSoc: from,
        toSoc: s.target_soc_pct,
        avgPrice: s.price_eur_per_kwh,
        feedIn: s.allow_grid_feed_in === true,
      });
    }
  }
  return runs;
}

function UpcomingList({ slots, liveSoc = null, max = 6 }: {
  slots: ForecastSlot[]; liveSoc?: number | null; max?: number;
}) {
  const runs = toRuns(slots, liveSoc).filter((r) => r.action !== "rust").slice(0, max);
  if (runs.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Komende acties</SectionTitle>
      <Card className="divide-y divide-line">
        {runs.map((r) => (
          <div key={r.start} className="px-4 py-3 flex items-center gap-3">
            <Pill tone={ACTION_TONE[r.action]}>{ACTION_LABEL[r.action]}</Pill>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium tabular">
                {new Date(r.start).toDateString() !== new Date().toDateString() && (
                  <span className="text-ink-mute font-normal">
                    {new Date(r.start).toLocaleDateString("nl-NL", { weekday: "short" })}{" "}
                  </span>
                )}
                {clock(r.start)} - {clock(new Date(r.end * 1000).toISOString())}
              </div>
              <div className="text-xs text-ink-mute tabular">
                {`${Math.round(r.fromSoc)}% → ${Math.round(r.toSoc)}%`}
                {r.avgPrice != null ? ` · ~${formatPricePerKwh(r.avgPrice)}` : ""}
                {r.feedIn && r.action === "ontladen" ? " · teruglevering" : ""}
              </div>
            </div>
          </div>
        ))}
      </Card>
    </section>
  );
}

/* ---------------- 48h outlook: solar vs consumption ---------------- */

/** Stacked forecast bars from the Slim Stroom plan itself: house load
 *  (incl. heat pump) with the EV's planned charging stacked on top,
 *  PV as its own bar beside them. Hourly-averaged from the 15-min
 *  slots so ~40 h stays readable on a phone. Same axis idiom as
 *  OutlookChart (kW scale + midnight separators). */
function LegendSwatch({ color, label }: { color: string; label: string }) {
  // Inline background: tailwind's `bg-x/70` opacity modifier is a no-op
  // on our var()-based colour tokens (they carry no <alpha-value>), so
  // the swatches rendered invisible (2026-10-07).
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color, opacity: 0.85 }} />
      {label}
    </span>
  );
}

type BalanceRow = {
  t: number;
  // supply (kW): where the hour's energy comes from
  pv: number; battOut: number; gridIn: number;
  // demand (kW): where it goes
  house: number; ev: number; battIn: number; gridOut: number;
};

/**
 * Verwachte energiebalans: ONE chart per hour, supply above the zero
 * line (zon, ontladen, van net) mirrored by demand below (huis, auto,
 * laden, naar net); every hour balances by construction so both
 * stacks are equally tall. Replaced two charts on 2026-10-07.
 * docs/customer-app.md > Front-end > Prognose tab
 */
function BalanceChart({ loads }: { loads: EmsLoadPoint[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibrated = useRef<number | null>(null);

  const hasGrid = loads.some((p) => p.grid_w != null);

  // 15-min slots -> hourly means (kW); signed flows split per slot so
  // an hour that both charges and discharges shows both.
  const byHour = new Map<number, BalanceRow & { n: number }>();
  for (const pt of loads) {
    const h = pt.t - (pt.t % 3600);
    const b = byHour.get(h) ?? { t: h, pv: 0, battOut: 0, gridIn: 0, house: 0, ev: 0, battIn: 0, gridOut: 0, n: 0 };
    b.pv += pt.pv_w; b.house += pt.house_w; b.ev += pt.ev_w;
    b.gridIn += Math.max(pt.grid_w ?? 0, 0); b.gridOut += Math.max(-(pt.grid_w ?? 0), 0);
    b.battIn += Math.max(pt.battery_w ?? 0, 0); b.battOut += Math.max(-(pt.battery_w ?? 0), 0);
    b.n += 1;
    byHour.set(h, b);
  }
  const rows: BalanceRow[] = [...byHour.values()].sort((a, b) => a.t - b.t).map((b) => ({
    t: b.t,
    pv: b.pv / b.n / 1000, battOut: b.battOut / b.n / 1000, gridIn: b.gridIn / b.n / 1000,
    house: b.house / b.n / 1000, ev: b.ev / b.n / 1000, battIn: b.battIn / b.n / 1000, gridOut: b.gridOut / b.n / 1000,
  }));
  if (!rows.length) return null;
  const supply = (r: BalanceRow) => r.pv + r.battOut + r.gridIn;
  const demand = (r: BalanceRow) => r.house + r.ev + r.battIn + r.gridOut;
  const kwMax = Math.max(0.5, ...rows.map((r) => Math.max(supply(r), demand(r))));

  // Day totals (kWh) from the raw 15-min slots - local calendar days.
  type DayTotal = { label: string; pv: number; house: number; gridIn: number; gridOut: number };
  const byDay = new Map<string, DayTotal>();
  const todayKey = localDayString(0);
  const tomorrowKey = localDayString(-1);
  for (const pt of loads) {
    const d = new Date(pt.t * 1000);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = key === todayKey ? "vandaag" : key === tomorrowKey ? "morgen"
      : d.toLocaleDateString("nl-NL", { weekday: "long" });
    const b = byDay.get(key) ?? { label, pv: 0, house: 0, gridIn: 0, gridOut: 0 };
    const q = 0.25 / 1000; // W per quarter -> kWh
    b.pv += pt.pv_w * q; b.house += (pt.house_w + pt.ev_w) * q;
    b.gridIn += Math.max(pt.grid_w ?? 0, 0) * q; b.gridOut += Math.max(-(pt.grid_w ?? 0), 0) * q;
    byDay.set(key, b);
  }
  const dayTotals = [...byDay.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, v]) => v);

  const W = 700;
  const H = 240;
  const P = { left: 34, right: 8, top: 10, bottom: 30 };
  const n = rows.length;
  const slotW = (W - P.left - P.right) / n;
  const midY = P.top + (H - P.top - P.bottom) / 2;
  const half = (H - P.top - P.bottom) / 2;
  const px = (kw: number) => (kw / kwMax) * half;
  const barW = Math.max(1.5, slotW * 0.62);

  const days: { x: number; label: string }[] = [];
  rows.forEach((r, i) => {
    const d = new Date(r.t * 1000);
    if (d.getHours() === 0) {
      days.push({ x: P.left + i * slotW, label: d.toLocaleDateString("nl-NL", { weekday: "short" }) });
    }
  });

  const nowIdx = (() => {
    const nowH = Math.floor(Date.now() / 1000 / 3600) * 3600;
    const i = rows.findIndex((r) => r.t >= nowH);
    return i < 0 ? rows.length - 1 : i;
  })();

  function pointerToIdx(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const frac = (vbX - P.left) / (W - P.left - P.right);
    return Math.min(n - 1, Math.max(0, Math.floor(frac * n)));
  }
  function setFrom(clientX: number, isTouch: boolean) {
    const i = pointerToIdx(clientX);
    if (i == null) return;
    setHoverIdx(i);
    if (isTouch && i !== lastVibrated.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibrated.current = i;
    }
  }
  function clearScrub() {
    setHoverIdx(null);
    lastVibrated.current = null;
  }

  const focusIdx = Math.min(n - 1, Math.max(0, hoverIdx ?? nowIdx));
  const f = rows[focusIdx];
  const focusX = P.left + focusIdx * slotW + slotW / 2;
  const fmt1 = (v: number) => v.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const kw = (v: number) => `${v.toFixed(2)} kW`;

  // Stacked segments, drawn from the zero line outward. Order puts the
  // "own" energy nearest the line and the grid at the far end, so the
  // grid slice is the one that sticks out.
  const up: { key: keyof BalanceRow; color: string }[] = [
    { key: "pv", color: "var(--color-sun)" },
    { key: "battOut", color: "var(--color-accent)" },
    { key: "gridIn", color: "var(--color-grid)" },
  ];
  const down: { key: keyof BalanceRow; color: string }[] = [
    { key: "house", color: "var(--color-home)" },
    { key: "ev", color: "var(--color-ev)" },
    { key: "battIn", color: "var(--color-accent)" },
    { key: "gridOut", color: "var(--color-grid)" },
  ];

  const readout: { label: string; value: string; color?: string }[] = [];
  if (f.pv >= 0.01) readout.push({ label: "zon", value: kw(f.pv), color: "var(--color-sun)" });
  readout.push({ label: "huis", value: kw(f.house) });
  if (f.ev >= 0.01) readout.push({ label: "auto", value: kw(f.ev), color: "var(--color-ev)" });
  if (f.battIn >= 0.01) readout.push({ label: "batterij laden", value: kw(f.battIn), color: "var(--color-accent)" });
  if (f.battOut >= 0.01) readout.push({ label: "batterij ontladen", value: kw(f.battOut), color: "var(--color-accent)" });
  if (f.gridIn >= 0.01) readout.push({ label: "van net", value: kw(f.gridIn), color: "var(--color-grid)" });
  if (f.gridOut >= 0.01) readout.push({ label: "naar net", value: kw(f.gridOut), color: "var(--color-grid)" });

  return (
    <div className="select-none">
      {/* dagtotalen */}
      <div className="flex flex-col gap-0.5 text-xs mb-2">
        {dayTotals.map((d) => (
          <div key={d.label} className="flex flex-wrap gap-x-3 text-ink-mute">
            <span className="font-semibold text-ink w-14">{d.label}</span>
            <span className="tabular">zon <span style={{ color: "var(--color-sun)" }}>{fmt1(d.pv)}</span></span>
            <span className="tabular">huis <span style={{ color: "var(--color-home)" }}>{fmt1(d.house)}</span></span>
            {hasGrid && (
              <span className="tabular">net ↓ {fmt1(d.gridIn)} ↑ {fmt1(d.gridOut)}</span>
            )}
            <span>kWh</span>
          </div>
        ))}
      </div>
      {/* readout - vaste hoogte zodat scrubben de layout niet verspringt */}
      <div className="flex flex-wrap items-baseline gap-x-3 text-xs mb-1 min-h-[1.25rem]">
        <span className="font-semibold tabular text-ink">
          {new Date(f.t * 1000).toLocaleString("nl-NL", {
            weekday: "short", hour: "2-digit", minute: "2-digit",
          })}
        </span>
        {readout.map((r) => (
          <span key={r.label} className="text-ink-mute">
            {r.label}{" "}
            <span className="font-medium tabular" style={{ color: r.color ?? "var(--color-ink)" }}>{r.value}</span>
          </span>
        ))}
        {hoverIdx == null && <span className="text-ink-mute">· nu</span>}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Verwachte energiebalans"
        data-swipe-consume="true"
        style={{ touchAction: "pan-y" }}
        onMouseMove={(e) => setFrom(e.clientX, false)}
        onMouseLeave={clearScrub}
        onTouchStart={(e) => setFrom(e.touches[0].clientX, true)}
        onTouchMove={(e) => setFrom(e.touches[0].clientX, true)}
        onTouchEnd={clearScrub}
        onTouchCancel={clearScrub}
      >
      {[{ y: midY - px(kwMax), label: `${kwMax.toFixed(1)} kW` }, { y: midY + px(kwMax), label: kwMax.toFixed(1) }].map((g) => (
        <g key={g.y}>
          <line x1={P.left} x2={W - P.right} y1={g.y} y2={g.y}
            stroke="var(--color-line)" strokeWidth={1} strokeDasharray="2 4" />
          <text x={P.left - 5} y={g.y} textAnchor="end" dominantBaseline="central"
            fontSize={9} fill="var(--color-ink-mute)" className="tabular">{g.label}</text>
        </g>
      ))}
      {days.map((d) => (
        <g key={d.x}>
          <line x1={d.x} x2={d.x} y1={P.top} y2={H - P.bottom}
            stroke="var(--color-line)" strokeWidth={1} />
          <text x={d.x + 4} y={H - P.bottom + 14} fontSize={9}
            fill="var(--color-ink-mute)">{d.label}</text>
        </g>
      ))}
      {/* focuslijn achter de staven */}
      <line x1={focusX} x2={focusX} y1={P.top} y2={H - P.bottom}
        stroke="var(--color-ink)" strokeWidth={1} opacity={0.45} />
      {rows.map((r, i) => {
        const x0 = P.left + i * slotW + (slotW - barW) / 2;
        const op = i === focusIdx ? 0.95 : 0.72;
        let yTop = midY;
        let yBot = midY;
        return (
          <g key={r.t}>
            {up.map((seg) => {
              const h = px(r[seg.key] as number);
              if (h < 0.3) return null;
              yTop -= h;
              return <rect key={seg.key} x={x0} y={yTop} width={barW} height={h} fill={seg.color} opacity={op} />;
            })}
            {down.map((seg) => {
              const h = px(r[seg.key] as number);
              if (h < 0.3) return null;
              const y = yBot;
              yBot += h;
              return <rect key={seg.key} x={x0} y={y} width={barW} height={h} fill={seg.color} opacity={op} />;
            })}
          </g>
        );
      })}
      {/* nullijn bovenop de staven */}
      <line x1={P.left} x2={W - P.right} y1={midY} y2={midY}
        stroke="var(--color-ink-mute)" strokeWidth={1} />
      <text x={P.left - 5} y={midY} textAnchor="end" dominantBaseline="central"
        fontSize={9} fill="var(--color-ink-mute)" className="tabular">0</text>
      </svg>
    </div>
  );
}

function OutlookChart({
  solar,
  consumption,
}: {
  solar: EnergyForecast | null;
  consumption: EnergyForecast | null;
}) {
  const t0 = solar?.t[0] ?? consumption?.t[0];
  if (t0 == null) return null;
  const axis = (solar ?? consumption)!.t;
  const kwhMax = Math.max(
    0.5,
    ...(solar?.kwh.filter((v): v is number => v != null) ?? []),
    ...(consumption?.kwh.filter((v): v is number => v != null) ?? []),
  );
  const W = 700;
  const H = 200;
  const P = { left: 30, right: 8, top: 10, bottom: 30 };
  const n = axis.length;
  const slotW = (W - P.left - P.right) / n;
  const y = (v: number) => P.top + (1 - v / kwhMax) * (H - P.top - P.bottom);
  const barW = Math.max(1.5, slotW * 0.38);

  // midnight separators + day labels
  const days: { x: number; label: string }[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(axis[i] * 1000);
    if (d.getHours() === 0) {
      days.push({
        x: P.left + i * slotW,
        label: d.toLocaleDateString("nl-NL", { weekday: "short" }),
      });
    }
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
      aria-label="Zon- en verbruiksverwachting">
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={P.left} x2={W - P.right} y1={y(kwhMax * f)} y2={y(kwhMax * f)}
            stroke="var(--color-line)" strokeWidth={1} strokeDasharray={f === 0 ? "" : "2 4"} />
          <text x={P.left - 5} y={y(kwhMax * f)} textAnchor="end" dominantBaseline="central"
            fontSize={9} fill="var(--color-ink-mute)" className="tabular">
            {(kwhMax * f).toFixed(1)}
          </text>
        </g>
      ))}
      {days.map((d) => (
        <g key={d.x}>
          <line x1={d.x} x2={d.x} y1={P.top} y2={H - P.bottom}
            stroke="var(--color-line)" strokeWidth={1} />
          <text x={d.x + 4} y={H - P.bottom + 14} fontSize={9}
            fill="var(--color-ink-mute)">{d.label}</text>
        </g>
      ))}
      {axis.map((ts, i) => {
        const s = solar?.kwh[i];
        const c = consumption?.kwh[i];
        const x0 = P.left + i * slotW + slotW * 0.1;
        return (
          <g key={ts}>
            {s != null && s > 0 && (
              <rect x={x0} y={y(s)} width={barW} height={Math.max(1, H - P.bottom - y(s))}
                rx={1} fill="var(--color-sun)" opacity={0.65} />
            )}
            {c != null && c > 0 && (
              <rect x={x0 + barW + 1} y={y(c)} width={barW}
                height={Math.max(1, H - P.bottom - y(c))}
                rx={1} fill="var(--color-home)" opacity={0.65} />
            )}
          </g>
        );
      })}
      {/* hour labels every 6h */}
      {axis.map((ts, i) => {
        const d = new Date(ts * 1000);
        if (d.getHours() % 6 !== 0 || d.getHours() === 0) return null;
        return (
          <text key={`h${ts}`} x={P.left + i * slotW + slotW / 2} y={H - P.bottom + 14}
            textAnchor="middle" fontSize={8.5} fill="var(--color-ink-mute)" className="tabular">
            {`${String(d.getHours()).padStart(2, "0")}u`}
          </text>
        );
      })}
    </svg>
  );
}

/* ---------------- helpers ---------------- */

const NL_CLOCK = new Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit" });
function clock(iso: string): string {
  return NL_CLOCK.format(new Date(iso));
}

function CrystalIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l5-8 4 5 4-9 5 12" />
    </svg>
  );
}
